import { useMemo } from 'react';
import classNames from 'classnames';
import type { ObjectSchema } from 'yup';
import { FormProvider, type Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { defaultValues, formSchema, type BuscadorEscriturasFormValues } from './config';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNButton from '@ctn/ui/components/ctn-button';

type BuscadorEscriturasFormProps = {
	readonly className?: string;
	readonly onSubmitSuccess?: (payload: BuscadorEscriturasFormValues) => void;
};

const classNameComponent = 'c-cnm-buscadores__form';

export const BuscadorEscriturasForm = ({ className, onSubmitSuccess }: BuscadorEscriturasFormProps) => {
	const wrapperClassName = classNames(classNameComponent, className);

	const resolver = useMemo<Resolver<BuscadorEscriturasFormValues>>(
		() =>
			yupResolver<BuscadorEscriturasFormValues>(
				formSchema as unknown as ObjectSchema<BuscadorEscriturasFormValues>,
			),
		[],
	);

	const methods = useForm<BuscadorEscriturasFormValues>({
		mode: 'onTouched',
		resolver,
		defaultValues,
	});

	const {
		control,
		formState: { errors, isSubmitting },
		handleSubmit,
	} = methods;

	const submit = handleSubmit((data: BuscadorEscriturasFormValues) => {
		const payload: BuscadorEscriturasFormValues = {
			...data,
		};
		onSubmitSuccess?.(payload);
	});

	return (
		<div className={wrapperClassName}>
			<h3 className={`${classNameComponent}-title`}>Buscador de escrituras públicas</h3>

			<FormProvider {...methods}>
				<form noValidate className="[ o-grid ] [ u-mt-16 ]" onSubmit={(e) => e.preventDefault()}>
					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="name"
								label="Nombre notario autorizante escritura"
								classNameField={`${classNameComponent}-field`}
								classNameInput={`${classNameComponent}-input`}
								error={errors.name?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="surname"
								label="Apellido notario autorizante escritura"
								classNameField={`${classNameComponent}-field`}
								classNameInput={`${classNameComponent}-input`}
								error={errors.surname?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="notariaPoblacion"
								label="Apellido notario autorizante escritura"
								classNameField={`${classNameComponent}-field`}
								classNameInput={`${classNameComponent}-input`}
								error={errors.notariaPoblacion?.message}
							/>
						</div>
					</div>

					<div className={`${classNameComponent}-extra o-grid__row`}>
						<div className="o-grid__col-12">
							<p className={`${classNameComponent}-textInfo`}>
								Protocolos consulares depositados en el archivo general de protocolos
							</p>
						</div>
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="consuladoCiudad"
								label="Apellido notario autorizante escritura"
								classNameField={`${classNameComponent}-field`}
								classNameInput={`${classNameComponent}-input`}
								error={errors.consuladoCiudad?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12 o-grid__col-6@landscape o-grid__col-3@desktop">
							<CTNButton
								type="button"
								variant="solid"
								mode="primary"
								className={`${classNameComponent}-btn`}
								label="Buscar"
								onClick={submit}
							/>
						</div>
					</div>
				</form>
			</FormProvider>
		</div>
	);
};
